import React, { useState } from 'react';
import styled from 'styled-components';
import { MenuItem, Select } from '@mui/material';
import { SPACERS } from 'shared/design-system/settings/variables/Spacers';
import TrueImpactText from 'shared/design-system/base/TrueImpactText';
import TrueImpactButton from 'shared/design-system/components/TrueImpactButton';
import TrueImpactTextfield from 'shared/design-system/components/TrueImpactTextfield';
import {
  ErrorBanner,
  SuccessBanner,
  CharCount,
  RatingStars,
} from './shared';
import { postFeedback, FeedbackResponse, FeedbackType, MatchResult } from './api';
import { MAX_FEEDBACK_COMMENT } from './constants';

const FieldMeta = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin: 4px 0;
`;

const Row = styled.div`
  display: flex;
  align-items: center;
  gap: ${SPACERS.small};
  margin: ${SPACERS.tiny} 0;
  flex-wrap: wrap;
`;

interface FeedbackFormProps {
  matchId: number;
  matches?: MatchResult[];
  title?: string;
  onSubmitted?: (feedback: FeedbackResponse) => void;
}

const FeedbackForm: React.FC<FeedbackFormProps> = ({
  matchId,
  matches,
  title = 'How good were these matches?',
  onSubmitted,
}) => {
  const [feedbackType, setFeedbackType] = useState<FeedbackType>('rating');
  const [selectedTemplateId, setSelectedTemplateId] = useState<number | ''>('');
  const [rating, setRating] = useState(0);
  const [comment, setComment] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [sent, setSent] = useState(false);

  const send = async () => {
    if (feedbackType === 'rating' && rating < 1) return;
    setLoading(true);
    setError(null);
    try {
      const res = await postFeedback(
        matchId,
        feedbackType === 'rating' ? rating : 1,
        comment.trim() || undefined,
        feedbackType,
        selectedTemplateId !== '' ? selectedTemplateId : undefined,
      );
      setSent(true);
      onSubmitted?.(res);
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (err: any) {
      setError(err?.message || 'Failed to submit feedback.');
    } finally {
      setLoading(false);
    }
  };

  if (sent) {
    return (
      <SuccessBanner style={{ marginBottom: 0 }}>
        Thanks — your feedback was submitted.
      </SuccessBanner>
    );
  }

  return (
    <>
      <TrueImpactText variant="body-small-bold">{title}</TrueImpactText>
      <Row>
        <Select
          size="small"
          value={feedbackType}
          onChange={e => setFeedbackType(e.target.value as FeedbackType)}
          style={{ fontSize: 13, minWidth: 200 }}
        >
          <MenuItem value="rating">Rating</MenuItem>
          <MenuItem value="no_match_closest_fit">No match — closest fit</MenuItem>
          <MenuItem value="no_match_support">No match — need support</MenuItem>
        </Select>
        {feedbackType === 'rating' && (
          <RatingStars value={rating} onChange={setRating} size={22} />
        )}
      </Row>
      {matches && matches.length > 0 && (
        <Row>
          <TrueImpactText variant="body-small" style={{ flexShrink: 0 }}>
            Selected template:
          </TrueImpactText>
          <Select
            size="small"
            displayEmpty
            value={selectedTemplateId}
            onChange={e => setSelectedTemplateId(e.target.value as number | '')}
            style={{ fontSize: 13, flex: 1, minWidth: 180 }}
          >
            <MenuItem value=""><em>None</em></MenuItem>
            {matches.map(m => (
              <MenuItem key={m.template_id} value={m.template_id}>
                #{m.rank} {m.name || 'Unnamed'}
              </MenuItem>
            ))}
          </Select>
        </Row>
      )}
      <TrueImpactTextfield
        isTextarea
        height={70}
        placeholder="Optional comment — what worked, what didn't…"
        value={comment}
        maxLength={MAX_FEEDBACK_COMMENT}
        onChange={v => setComment(v)}
      />
      <FieldMeta>
        <CharCount over={comment.length >= MAX_FEEDBACK_COMMENT}>
          {comment.length} / {MAX_FEEDBACK_COMMENT}
        </CharCount>
        <TrueImpactButton
          variant="secondary-small"
          disabled={loading || (feedbackType === 'rating' && rating < 1)}
          onClick={send}
        >
          {loading ? 'Sending…' : 'Send Feedback'}
        </TrueImpactButton>
      </FieldMeta>
      {error && <ErrorBanner>{error}</ErrorBanner>}
    </>
  );
};

export default FeedbackForm;
